import React from 'react';
import { FailedIndicator } from './FailedIndicator';
import { useJobStatus } from '../hooks/queries/useJobStatus';
import { TranscriptionJobStatus } from '../types/transcription';

interface RequestFailedIndicatorProps {
  requestId: string;
  stage: string | undefined;
  size?: 'small' | 'medium' | 'large';
  enabled?: boolean; // Allow parent to control when to fetch
}

/**
 * Component that fetches and displays the failed indicator for a request with a failed transcription job.
 * Only shows when: stage is DRAFT AND there's a failed transcription job.
 */
export const RequestFailedIndicator: React.FC<RequestFailedIndicatorProps> = ({
  requestId,
  stage,
  size = 'small',
  enabled = true,
}) => {
  // Parse request ID to number (handle offline requests that start with 'o-')
  const requestIdNum = requestId.startsWith('o-') ? undefined : parseInt(requestId, 10);
  
  // Only fetch if we have a valid numeric request ID, enabled is true, and stage is DRAFT
  // (failed jobs only occur when stage is DRAFT)
  const isDraftStage = stage === 'DRAFT';
  const { data: jobStatus } = useJobStatus(requestIdNum, {
    enabled: enabled && !!requestIdNum && isDraftStage,
    refetchInterval: false, // Don't poll - we only need the status which doesn't change
    retry: false, // Don't retry on 404 (no job exists) - this is expected for most requests
  });

  // Show failed indicator when: stage is DRAFT AND there's a failed transcription job
  const hasFailedJob = jobStatus?.status === TranscriptionJobStatus.FAILED;
  const showFailedIndicator = isDraftStage && hasFailedJob;

  if (!showFailedIndicator) {
    return null;
  }

  return <FailedIndicator size={size} errorMessage={jobStatus?.errorMessage || null} />;
};

